import { IAccountUpdate, IDevice, IInventoryItem, IInventoryTransaction, 
        IInventoryTransactionBatch, IInventoryTransactionBatchCreate, 
        IInventoryTransactionBatchUpdate, 
        IMenu, IMenuCategory, IMenuCounter, 
        IMenuItem, IMenuItemCreate, IMenuItemCreateFull, 
        IMenuItemUpdateFull, IMenuLinkCreate, IMenuLinkReduced, 
        IMenuUpdate, IOrderMetricsParams, 
        ISponsor, ISponsorCreate, ISponsorUpdate, 
        ITable, ITableCreate, ITableUpdate, 
        IZone, IZoneCreate, IZoneUpdate } from "@/interfaces";
import {
    IEmployee,
    IEmployeeCreate,
    IEmployeeUpdate,
    IOwner,
    IMenuCategoryCreate,
    IFileId,
} from "./interfaces/index";
import axios from "axios";
import { apiUrl } from "@/env";
import { getLocalToken } from "@/utils";
import {
    IUserProfile,
    IUserProfileUpdate,
    IUserProfileCreate,
    IRoleAdmin,
    ILanguage,
    ILanguageCreate,
    ILanguageUpdate,
    ISystemFeature,
    ISystemFeatureCreate,
    ISystemFeatureUpdate,
    IAction,
    IActionCreate,
    IActionUpdate,
    IActionUpdateMove,
    IRoleAdminCreate,
    IRoleAdminUpdate,
    ICurrency,
    ICurrencyCreate,
    ICurrencyUpdate,
    ICountry,
    IState,
    ICity,
    IAccountPriceCreate,
    IAccountProfileCreate,
    IAccountProfileUpdate,
    IAccountProfile,
    IAccountPrice,
    IAccountProfileOpen,
    IAccountCreateDash,
    IAccount,
    IIngredient,
    IIngredientCreate,
} from "./interfaces";

function authHeaders(token: string, extra = {}) {
    const basic = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const obj = Object.assign(basic, extra);
    
    return obj;
}

export const api = {
    async logInGetToken(username: string, password: string) {
        const params = new URLSearchParams();
        params.append("username", username);
        params.append("password", password);

        return axios.post(`${apiUrl}/api/v1/login/access-token`, params);
    },
    async getMe(token: string) {
        return axios.get<IUserProfile>(
            `${apiUrl}/api/v1/users/me`,
            authHeaders(token)
        );
    },
    async updateMe(token: string, data: IUserProfileUpdate) {
        return axios.put<IUserProfile>(
            `${apiUrl}/api/v1/users/me`,
            data,
            authHeaders(token)
        );
    },
    async getUsers(token: string) {
        return axios.get<IUserProfile[]>(
            `${apiUrl}/api/v1/users/`,
            authHeaders(token)
        );
    },
    async updateUser(token: string, userId: number, data: IUserProfileUpdate) {
        return axios.put(
            `${apiUrl}/api/v1/users/${userId}`,
            data,
            authHeaders(token)
        );
    },
    async createUser(token: string, data: IUserProfileCreate) {
        return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token));
    },
    async passwordRecovery(email: string) {
        return axios.post(`${apiUrl}/api/v1/password-recovery/${email}`);
    },
    async resetPassword(password: string, token: string) {
        return axios.post(`${apiUrl}/api/v1/reset-password/`, {
            new_password: password,
            token,
        });
    },
    async getRoles(token: string, staff_only: boolean | null = null) {
        if (staff_only !== null) {
            return axios.get<IRoleAdmin[]>(
                `${apiUrl}/api/v1/roles/`,
                authHeaders(token, { params: { staff_only: staff_only } })
            );
        } else {
            return axios.get<IRoleAdmin[]>(
                `${apiUrl}/api/v1/roles/`,
                authHeaders(token)
            );
        }
    },
    async getRole(token: string, id: number) {
        return axios.get<IRoleAdmin>(
            `${apiUrl}/api/v1/roles/${id}`,
            authHeaders(token)
        );
    },
    async createRole(token: string, data: IRoleAdminCreate) {
        return axios.post(`${apiUrl}/api/v1/roles/`, data, authHeaders(token));
    },
    async updateRole(token: string, id: number, data: IRoleAdminUpdate) {
        return axios.put(
            `${apiUrl}/api/v1/roles/${id}`,
            data,
            authHeaders(token)
        );
    },
    async deleteRole(token: string, id: number) {
        return axios.delete(`${apiUrl}/api/v1/roles/${id}`, authHeaders(token));
    },

    // Languages api
    async getLanguages() {
        return axios.get<ILanguage[]>(`${apiUrl}/api/v1/languages/`);
    },
    async getLanguage(token: string, id: number) {
        return axios.get<ILanguage>(
            `${apiUrl}/api/v1/languages/${id}`,
            authHeaders(token)
        );
    },
    async deleteLanguage(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/languages/${id}`,
            authHeaders(token)
        );
    },
    async createLanguage(token: string, data: ILanguageCreate) {
        return axios.post(
            `${apiUrl}/api/v1/languages/`,
            data,
            authHeaders(token)
        );
    },
    async updateLanguage(token: string, id: number, data: ILanguageUpdate) {
        return axios.put(
            `${apiUrl}/api/v1/languages/${id}`,
            data,
            authHeaders(token)
        );
    },
    // System features api
    async getSystemFeatures(token: string) {
        return axios.get<ISystemFeature[]>(
            `${apiUrl}/api/v1/features/`,
            authHeaders(token)
        );
    },
    async getSystemFeaturesByStaff(token: string, theStaff: boolean) {
        const config = authHeaders(token);
        config[`params`] = { staff: theStaff };
        return axios.get<ISystemFeature[]>(
            `${apiUrl}/api/v1/features/`,
            config
        );
    },
    async getSystemFeature(token: string, id: number) {
        return axios.get<ISystemFeature>(
            `${apiUrl}/apiv/v1/features/${id}`,
            authHeaders(token)
        );
    },
    async deleteSystemFeature(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/features/${id}`,
            authHeaders(token)
        );
    },
    async createSystemFeature(token: string, data: ISystemFeatureCreate) {
        return axios.post(
            `${apiUrl}/api/v1/features/`,
            data,
            authHeaders(token)
        );
    },
    async updateSystemFeature(
        token: string,
        id: number,
        data: ISystemFeatureUpdate
    ) {
        return axios.put(
            `${apiUrl}/api/v1/features/${id}`,
            data,
            authHeaders(token)
        );
    },
    // Actions api
    async getActions(token: string) {
        return axios.get<IAction[]>(
            `${apiUrl}/api/v1/actions/`,
            authHeaders(token)
        );
    },
    async getActionsByFeature(token: string, featureId: number) {
        const config = authHeaders(token);
        config[`params`] = { by_feature_id: featureId };
        return axios.get<IAction[]>(`${apiUrl}/api/v1/actions/`, config);
    },
    async getAction(token: string, id: number) {
        return axios.get<IAction>(
            `${apiUrl}/apiv/v1/actions/${id}`,
            authHeaders(token)
        );
    },
    async deleteAction(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/actions/${id}`,
            authHeaders(token)
        );
    },
    async createAction(token: string, data: IActionCreate) {
        return axios.post(
            `${apiUrl}/api/v1/actions/`,
            data,
            authHeaders(token)
        );
    },
    async updateAction(token: string, id: number, data: IActionUpdate) {
        return axios.put(
            `${apiUrl}/api/v1/actions/${id}`,
            data,
            authHeaders(token)
        );
    },
    async updateActionFeature(
        token: string,
        id: number,
        data: IActionUpdateMove
    ) {
        return axios.put(
            `${apiUrl}/api/v1/actions/${id}`,
            data,
            authHeaders(token)
        );
    },
    async getPhoto(token: string, id: number) {
        return axios.get(
            `${apiUrl}/api/v1/images/profile/${id}`,
            authHeaders(token)
        );
    },
    // Currencies api
    async getCurrencies() {
        return axios.get<ICurrency[]>(`${apiUrl}/api/v1/currencies/`);
    },
    async getCurrency(token: string, id: number) {
        return axios.get<ICurrency>(
            `${apiUrl}/api/v1/currencies/${id}`,
            authHeaders(token)
        );
    },
    async deleteCurrency(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/currencies/${id}`,
            authHeaders(token)
        );
    },
    async createCurrency(token: string, data: ICurrencyCreate) {
        return axios.post(
            `${apiUrl}/api/v1/currencies/`,
            data,
            authHeaders(token)
        );
    },
    async updateCurrency(token: string, id: number, data: ICurrencyUpdate) {
        return axios.put(
            `${apiUrl}/api/v1/currencies/${id}`,
            data,
            authHeaders(token)
        );
    },
    // Countries api
    async getCountries() {
        return axios.get<ICountry[]>(`${apiUrl}/api/v1/countries/`);
    },
    async getStates(countryId: number) {
        return axios.get<IState[]>(
            `${apiUrl}/api/v1/countries/${countryId}/states/`
        );
    },
    async getCities(stateId: number) {
        return axios.get<IState[]>(
            `${apiUrl}/api/v1/countries/state/${stateId}/cities/`
        );
    },
    // Account profile api
    async getAccountProfiles(token: string) {
        return axios.get<IAccountProfile[]>(
            `${apiUrl}/api/v1/account_profiles/`,
            authHeaders(token)
        );
    },
    async getAccountProfile(token: string, id: number) {
        return axios.get<IAccountProfile>(
            `${apiUrl}/api/v1/account_profiles/${id}`
        );
    },
    async createAccountProfile(
        token: string,
        payload: { profile: IAccountProfileCreate; price: IAccountPriceCreate }
    ) {
        return axios.post<IAccountProfile>(
            `${apiUrl}/api/v1/account_profiles/`,
            payload,
            authHeaders(token)
        );
    },
    async updateAccountProfile(
        token: string,
        id: number,
        data: IAccountProfileUpdate
    ) {
        return axios.put<IAccountProfile>(
            `${apiUrl}/api/v1/account_profiles/${id}`,
            data,
            authHeaders(token)
        );
    },
    async deleteAccountProfile(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/account_profiles/${id}`,
            authHeaders(token)
        );
    },
    async getAccountProfilesOpen() {
        return axios.get<IAccountProfileOpen[]>(
            `${apiUrl}/api/v1/account_profiles/open`
        );
    },
    //Account api
    async createAccount(payload: IAccountCreateDash) {
        return axios.post<IAccount>(`${apiUrl}/api/v1/accounts/new`, payload);
    },

    async getAccounts(token: string) {
        return axios.get<IAccount[]>(
            `${apiUrl}/api/v1/accounts/`,
            authHeaders(token)
        );
    },

    async getAccount(token: string, id: number) {
        return axios.get<IAccount>(
            `${apiUrl}/api/v1/accounts/${id}`,
            authHeaders(token)
        );
    },

    async updateAccount(token: string, id: number, payload: IAccountUpdate) {
        return axios.put<IAccount>(
            `${apiUrl}/api/v1/accounts/${id}`,
            payload,
            authHeaders(token)
        );
    },

    async deleteAccount(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/accounts/${id}`,
            authHeaders(token)
        );
    },

    //Menu Api 1
    async getMenu(token: string, id: number) {
        return axios.get<IMenu>(
            `${apiUrl}/api/v1/menu/${id}`,
            authHeaders(token)
        );
    },

    async updateMenu(token: string, id: number, payload: IMenuUpdate) {
        return axios.put<IMenu>(
            `${apiUrl}/api/v1/menu/${id}`,
            payload,
            authHeaders(token)
        );
    },

    //Menu Api
    async addMenuItem(token: string, payload: IMenuItemCreateFull) {
        return axios.post<IMenuItem>(
            `${apiUrl}/api/v1/menu_items/`,
            payload,
            authHeaders(token)
        );
    },



    async getAllMenuItems(token: string, menu_id: number) {
        return axios.get<IMenuItem[]>(
            `${apiUrl}/api/v1/menu_items/`,
            authHeaders(token, { params: { menu_id: menu_id } })
        );
    },

    async getMenuItems(token: string, category_id: number) {
        return axios.get<IMenuItem[]>(
            `${apiUrl}/api/v1/menu_items/`,
            authHeaders(token, { params: { category_id: category_id } })
        );
    },

    async updateMenuItem(
        token: string,
        id: number,
        payload: IMenuItemUpdateFull
    ) {
        return axios.put<IMenuItem>(
            `${apiUrl}/api/v1/menu_items/${id}`,
            payload,
            authHeaders(token)
        );
    },

    async deleteMenuItem(token: string, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/menu_items/${id}`,
            authHeaders(token)
        );
    },



    //Categories
    async addCategory(token: string, payload: IMenuCategoryCreate) {
        return axios.post<IMenuCategory>(
            `${apiUrl}/api/v1/menu_categories/`,
            payload,
            authHeaders(token)
        );
    },

    async getCategories(token: string, menu_id: number) {
        return axios.get<IMenuCategory[]>(
            `${apiUrl}/api/v1/menu_categories/`,
            authHeaders(token, { params: { menu_id: menu_id } })
        );
    },
    //Employees Api
    async addEmployee(
        token: string,
        payload: IEmployeeCreate,
        account_id: number
    ) {
        return axios.post<IEmployee>(
            `${apiUrl}/api/v1/employees/`,
            payload,
            authHeaders(token, { params: { account_id: account_id } })
        );
    },

    async getEmployees(token: string, account_id: number) {
        return axios.get<IEmployee[]>(
            `${apiUrl}/api/v1/employees/`,
            authHeaders(token, { params: { account_id: account_id } })
        );
    },

    async getEmployee(
        token: string,
        id: number,
        account_id: number | null = null
    ) {
        if (account_id) {
            return axios.get<IEmployee>(
                `${apiUrl}/api/v1/employees/${id}`,
                authHeaders(token, { params: { account_id: account_id } })
            );
        } else {
            return axios.get<IEmployee>(
                `${apiUrl}/api/v1/employees/${id}`,
                authHeaders(token)
            );
        }
    },

    async updateEmployee(
        token: string,
        account_id: number,
        id: number,
        payload: IEmployeeUpdate
    ) {
        return axios.put<IEmployee>(
            `${apiUrl}/api/v1/employees/${id}`,
            payload,
            authHeaders(token, { params: { account_id: account_id } })
        );
    },

    async deleteEmployee(token: string, account_id: number, id: number) {
        return axios.delete(
            `${apiUrl}/api/v1/employees/${id}`,
            authHeaders(token, { params: { account_id: account_id } })
        );
    },

    //Owner Api
    async getOwnerProfile(token: string) {
        return axios.get<IOwner>(
            `${apiUrl}/api/v1/owners/me`,
            authHeaders(token)
        );
    },

    //Upload Files
    async uploadProfilePicture(token: string, payload: FormData) {
        return axios.post<IFileId>(
            `${apiUrl}/api/v1/images/person`,
            payload,
            authHeaders(token)
        );
    },

    async uploadMenuImages(token: string, upto: string | undefined, payload: FormData) {

        return axios.post<IFileId[]>(
            `${apiUrl}/api/v1/images/image`,
            payload,
            authHeaders(token, { params: { upto: upto } })
        );
    },
    //Ingredients API
    async getIngredients(token: string, limit: number, filter: string | null, lang: string) {
        return axios.get<IIngredient[]>(`${apiUrl}/api/v1/ingredients/`,
            authHeaders(token, { params: { limit: limit, filter: filter, lang: lang } }));
    },

    async getIngredient(token: string, id: number) {
        return axios.get<IIngredient>(`${apiUrl}/api/v1/ingredients/${id}`,
            authHeaders(token));
    },

    async addIngredient(token: string, payload: IIngredientCreate) {
        return axios.post<IIngredient>(`${apiUrl}/api/v1/ingredients/`, payload, authHeaders(token));
    },
    //Menu Link API
    async createLink(token: string, payload: IMenuLinkCreate) {
        return axios.post<IMenuLinkReduced>(`${apiUrl}/api/v1/menu_links/`, payload, authHeaders(token));
    },
    //Admin Zone API
    async getZones(token: string, branch_id: number) {
        return axios.get<IZone[]>(`${apiUrl}/api/v1/zones/`, authHeaders(token, { params: { branch_id: branch_id } }));
    },
    async getZone(token: string, id: number) {
        return axios.get<IZone>(`${apiUrl}/api/v1/zones/${id}`, authHeaders(token));
    },
    async addZone(token: string, payload: IZoneCreate) {
        return axios.post<IZone>(`${apiUrl}/api/v1/zones/`, payload, authHeaders(token));
    },
    async updateZone(token: string, id: number, payload: IZoneUpdate) {
        return axios.put<IZone>(`${apiUrl}/api/v1/zones/${id}`, payload, authHeaders(token));
    },
    async deleteZone(token: string, id: number) {
        return axios.delete(`${apiUrl}/api/v1/zones/${id}`, authHeaders(token));
    },
    //Admin Table API
    async getTables(token: string, branch_id: number) {
        return axios.get<ITable[]>(`${apiUrl}/api/v1/tables/`, authHeaders(token, { params: { branch_id: branch_id } }));
    },
    async getTable(token: string, id: number) {
        return axios.get<ITable>(`${apiUrl}/api/v1/tables/${id}`, authHeaders(token));
    },
    async addTable(token: string, payload: ITableCreate) {
        return axios.post<ITable>(`${apiUrl}/api/v1/tables/`, payload, authHeaders(token));
    },
    async updateTable(token: string, id: number, payload: ITableUpdate) {
        return axios.put<ITable>(`${apiUrl}/api/v1/tables/${id}`, payload, authHeaders(token));
    },
    async deleteTable(token: string, id: number) {
        return axios.delete(`${apiUrl}/api/v1/tables/${id}`, authHeaders(token));
    },
    async getTablesQRCodes(token: string, branch_id: number) {
        return axios.get(`${apiUrl}/api/v1/tables/qrcodes/${branch_id}`, authHeaders(token));
    },
    async generateTablesQRCodes(token: string, branch_id: number, qr_code_style: string) {
        return axios.post(`${apiUrl}/api/v1/tables/qrcodes/${branch_id}`, {}, authHeaders(token, { params: { qr_code_style: qr_code_style } }));
    },
    //Menu counter
    async getMenuCounter(token: string, menuId: number) {
        return axios.get<IMenuCounter>(`${apiUrl}/api/v1/menu/${menuId}/counters`, authHeaders(token));
    },

    //Devices
    async getDevicesByAccount(token: string, account_id: number) {
        return axios.get<IDevice[]>(`${apiUrl}/api/v1/devices/`, authHeaders(token, { params: { account_id: account_id } }));
    },

    async getDevicesByBranch(token: string, branch_id: number) {
        return axios.get<IDevice[]>(`${apiUrl}/api/v1/devices/`, authHeaders(token, { params: { branch_id: branch_id } }));
    },
    async locateDevice(token: string, device_id: number) {
        return axios.post<IDevice>(`${apiUrl}/api/v1/devices/${device_id}/buzz`, { intensity: 100 }, authHeaders(token));
    },

    //Sponsor API
    async getSponsors(token: string) {
        return axios.get<ISponsor[]>(`${apiUrl}/api/v1/sponsors/`, authHeaders(token));
    },
    async getSponsor(token: string, id: number) {
        return axios.get<ISponsor>(`${apiUrl}/api/v1/sponsors/${id}`, authHeaders(token));
    },
    async addSponsor(token: string, payload: ISponsorCreate) {
        return axios.post<ISponsor>(`${apiUrl}/api/v1/sponsors/`, payload, authHeaders(token));
    },
    async updateSponsor(token: string, id: number, payload: ISponsorUpdate) {
        return axios.put<ISponsor>(`${apiUrl}/api/v1/sponsors/${id}`, payload, authHeaders(token));
    },
    async deleteSponsor(token: string, id: number) {
        return axios.delete(`${apiUrl}/api/v1/sponsors/${id}`, authHeaders(token));
    },

    //Inventory API
    async getInventory(token: string, branch_id: number) {
        return axios.get<IInventoryItem[]>(`${apiUrl}/api/v1/inventory/`, authHeaders(token, { params: { branch_id: branch_id } }));
    },
    async getInventoryItem(token: string, id: number) {
        return axios.get<IInventoryItem>(`${apiUrl}/api/v1/inventory/${id}`, authHeaders(token));
    },
    async addInventoryItem(token: string, payload: IInventoryItem) {
        return axios.post<IInventoryItem>(`${apiUrl}/api/v1/inventory/`, payload, authHeaders(token));
    },
    async updateInventoryItem(token: string, id: number, payload: IInventoryItem) {
        return axios.put<IInventoryItem>(`${apiUrl}/api/v1/inventory/${id}`, payload, authHeaders(token));
    },
    async deleteInventoryItem(token: string, id: number) {
        return axios.delete(`${apiUrl}/api/v1/inventory/${id}`, authHeaders(token));
    },
    async syncInventory(token: string, branch_id: number) {
        return axios.post(`${apiUrl}/api/v1/inventory/sync`, { branch_id: branch_id }, authHeaders(token));
    },

    //InventoryTransactionBatch API /api/v1/inventory/transactionbatches endpoint
    async getInventoryTransactionBatches(token: string, branch_id: number) {
        return axios.get<IInventoryTransactionBatch[]>(`${apiUrl}/api/v1/inventory/transactionbatches/`, authHeaders(token, { params: { branch_id: branch_id } }));
    },
    async getInventoryTransactionBatch(token: string, id: number) {
        return axios.get<IInventoryTransactionBatch>(`${apiUrl}/api/v1/inventory/transactionbatches/${id}`, authHeaders(token));
    },
    async getLastOpenInventoryTransactionBatch(token: string, branch_id: number) {
        return axios.get<IInventoryTransactionBatch>(`${apiUrl}/api/v1/inventory/transactionbatches/last`, authHeaders(token, { params: { branch_id: branch_id } }));
    },
    async addInventoryTransactionBatch(token: string, payload: IInventoryTransactionBatchCreate) {
        return axios.post<IInventoryTransactionBatch>(`${apiUrl}/api/v1/inventory/transactionbatches/`, payload, authHeaders(token));
    },
    async updateInventoryTransactionBatch(token: string, id: number, payload: IInventoryTransactionBatchUpdate) {
        return axios.put<IInventoryTransactionBatch>(`${apiUrl}/api/v1/inventory/transactionbatches/${id}`, payload, authHeaders(token));
    },
    async closeInventoryTransactionBatch(token: string, id: number) {
        return axios.put<IInventoryTransactionBatch>(`${apiUrl}/api/v1/inventory/transactionbatches/${id}/close`, {}, authHeaders(token));
    },

    //InventoryTransaction API /api/v1/inventory/transactions endpoint
    async getInventoryTransactions(token: string, batch_id: number) {
        return axios.get<IInventoryTransaction[]>(`${apiUrl}/api/v1/inventory/transactions/by`, authHeaders(token, { params: { batch_id: batch_id } }));
    },
    async getInventoryTransaction(token: string, id: number) {
        return axios.get<IInventoryTransaction>(`${apiUrl}/api/v1/inventory/transactions/${id}`, authHeaders(token));
    },
    async addInventoryTransaction(token: string, payload: IInventoryTransactionBatchCreate) {
        return axios.post<IInventoryTransaction>(`${apiUrl}/api/v1/inventory/transactions/`, payload, authHeaders(token));
    },
    async updateInventoryTransaction(token: string, id: number, payload: IInventoryTransactionBatchUpdate) {
        return axios.put<IInventoryTransaction>(`${apiUrl}/api/v1/inventory/transactions/${id}`, payload, authHeaders(token));
    },

    //Dashboard metrics

    async getOrdersMetrics(token: string, metrics_params: IOrderMetricsParams) {
        return axios.get(`${apiUrl}/api/v1/orders/metrics`, authHeaders(token, { params: metrics_params }));
    }

};
